import type { CleaningScopeResponse } from '@/shared/api';
import { Button } from '@/shared/components';
import { useOnline } from '@/shared/hooks/useOnline';

interface CleaningScopeProps {
  groups: CleaningScopeResponse[];
  selectedId: string | null;
  select: (id: string | null) => void;
}

export function CleaningScopePicker({ groups, selectedId, select }: CleaningScopeProps) {
  return (
    <label className="text-ink-soft flex min-w-56 flex-col gap-1 text-xs font-semibold">
      Objekt
      <select
        aria-label="Objekt"
        value={selectedId ?? ''}
        onChange={(event) => select(event.target.value || null)}
        className="border-line rounded-control text-ink focus-visible:outline-forest min-h-11 border bg-white px-3 text-sm"
      >
        <option value="">Objekt wählen</option>
        {groups.map((group) => (
          <option key={group.id} value={group.id}>
            {group.name}
          </option>
        ))}
      </select>
    </label>
  );
}

const CARD =
  'border-hairline rounded-tile mx-auto flex max-w-md flex-col gap-3 border bg-white p-5';

/**
 * Ohne gewähltes Objekt gibt es keine Reinigung: sie gilt immer für genau ein Objekt und dessen
 * Ortszeit. Ladefehler, keine Zuweisung und Auswahl sind drei verschiedene Sätze.
 */
export function CleaningScopeRequired({
  groups,
  loading,
  error,
  select,
  retry,
}: {
  groups: CleaningScopeResponse[];
  loading: boolean;
  error: boolean;
  select: (id: string | null) => void;
  retry: () => void;
}) {
  const online = useOnline();
  if (error)
    return (
      <div role="alert" className={CARD}>
        <b className="text-[15px]">Objekte konnten nicht geladen werden</b>
        <p className="text-ink-soft text-sm">Prüf die Verbindung und versuch es noch einmal.</p>
        <Button variant="secondary" size="lg" className="self-start" onClick={retry}>
          Erneut laden
        </Button>
      </div>
    );
  // Ohne Netz pausiert die Abfrage: das ist weder „lädt“ noch „kein Objekt zugewiesen“.
  if (!online && groups.length === 0)
    return (
      <p role="status" className={`${CARD} text-ink-soft text-sm`}>
        Keine Verbindung – deine Objekte laden, sobald du wieder Netz hast.
      </p>
    );
  if (loading)
    return (
      <p role="status" className={`${CARD} text-ink-soft text-sm`}>
        Objekte werden geladen …
      </p>
    );
  if (groups.length === 0)
    return (
      <div className={CARD}>
        <b className="text-[15px]">Dir ist noch kein Objekt zugewiesen</b>
        <p className="text-ink-soft text-sm">
          Die Verwaltung muss deinem Konto zuerst ein Apartment zuweisen; danach erscheinen hier
          deine Reinigungen.
        </p>
      </div>
    );
  return (
    <div className={CARD}>
      <b className="text-[15px]">Welches Objekt?</b>
      <p className="text-ink-soft text-sm">
        Die Reinigung gilt immer für genau ein Objekt und dessen Ortszeit.
      </p>
      <CleaningScopePicker groups={groups} selectedId={null} select={select} />
    </div>
  );
}
